import { useMutation } from "react-query";

import type {
  ResponseFormatType,
  ToolType,
  updateMessageProps,
} from "../../pages/playground/types";
import type { ErrorResponse } from "../../utils/PlaygroundUtils";
import { checkRestrictions, readStream } from "../../utils/PlaygroundUtils";

export interface IMessageHeader {
  hitType: string | null;
  xCache: string | null;
  similarityScore: string | null;
  cacheType: string;
  latency?: number;
  tokensPerSecond?: number | null;
}

export interface IError extends Partial<Error> {
  type: string;
  message: string;
  error: any;
  headers?: {
    latency: string;
  };
}

interface FetchQuestionsRequest {
  messages: {
    role: string;
    content?: string;
    tool_call_id?: string;
    tool_calls?: [
      {
        id: string;
        type: string;
        function: {
          name: string;
          arguments: string;
        };
      },
    ];
  }[];
  model: string;
  stream: boolean;
  seed?: number | null;
  tools?: ToolType["tool"][];
  stream_options?: {
    include_usage: boolean;
  };
  n?: number;
  response_format?: ResponseFormatType;
}

interface FetchQuestionsProps extends FetchQuestionsRequest {
  apiKey: string;
  cacheType: string;
  onUpdate: (message: updateMessageProps) => void;
  onComplete: () => void;
  onError: (error: IError) => void;
  onResponseError: (error: ErrorResponse) => void;
}

const queryUrl = `${process.env.REACT_APP_PROXY_API_URL}/v1/chat/completions`;

const fetchQuestions = async ({
  messages,
  model,
  apiKey,
  cacheType,
  seed,
  stream,
  tools,
  n,
  response_format,
  onUpdate,
  onComplete,
  onError,
  onResponseError,
}: FetchQuestionsProps): Promise<any> => {
  const controller = new AbortController();
  const signal = controller.signal;

  const headers: Record<string, string> = {
    "Content-Type": "application/json; charset=UTF-8",
    Accept: "application/json",
    "Accept-Language": "en-GB,en-US;q=0.9,en;q=0.8",
    "Sec-Fetch-Dest": "empty",
    "Sec-Fetch-Mode": "cors",
    "Sec-Fetch-Site": "cross-site",
    Authorization: `Bearer ${apiKey}`,
    "Cache-Control": cacheType,
  };

  const body: FetchQuestionsRequest = {
    messages,
    model: model,
    stream,
  };

  if (stream) {
    body["stream_options"] = { include_usage: true };
  }

  if (seed) {
    body["seed"] = seed;
  }

  if (tools?.length) {
    body["tools"] = tools;
  }

  if (n && n > 1) {
    body["n"] = n;
  }

  if (response_format?.type !== "text") {
    body["response_format"] =
      response_format?.type === "json_schema"
        ? response_format
        : { type: "json_object" };
  }

  const id = Date.now();

  await fetch(queryUrl, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(body),
    signal,
  })
    .then(async response => {
      if (!response.ok) {
        const data = await response.json();
        onError({
          type: "fetch",
          message: data.message ?? "",
          ...data,
          headers,
        });
      }
      if (response.ok && !stream) {
        const data = await response.json();
        const headers: IMessageHeader = {
          hitType: response.headers.get("Cogcache-Hit-Type"),
          xCache: response.headers.get("X-Cache"),
          similarityScore: response.headers.get(
            "Cogcache-Similarity-Match-Score",
          ),
          cacheType: cacheType,
        };

        if (data.usage) {
          headers.latency = data.usage.latency_time;
          headers.tokensPerSecond =
            Math.round(
              data.usage.completion_tokens / data.usage.latency_time,
            ) || null;
        }
        if (
          data.choices[0].finish_reason === "stop" ||
          data.choices[0].finish_reason === "tool_calls"
        ) {
          onUpdate({
            headers,
            message: data,
            id,
          });
        } else {
          const restriction = checkRestrictions(data.choices[0]);
          onResponseError({
            type: "restriction",
            message: `The system stopped generating a response since it contained ${restriction}.`,
            headers,
          });
        }
        onComplete();
      }
      if (response.ok && stream) {
        readStream({
          response,
          onUpdate,
          onComplete,
          onError: onResponseError,
          cacheType,
        }).catch(console.error);
      }
    })
    .catch((error: Partial<Error>) => {
      console.log("error", error);
      onError(error as IError);
    });
};

export const useFetchQuestions = () => {
  return useMutation({
    mutationFn: (query: FetchQuestionsProps) => {
      return fetchQuestions(query);
    },
  });
};

export const MAX_CHOICES = 12;
